/**
 * StatsAgent — data-enrichment layer for player statistics.
 *
 * Responsibility: fetch per-game stat breakdowns from the DB and inject them
 * as additional context into helpAgent, then let the LLM answer naturally.
 *
 * Season totals are already in ctx.teamContextForAgent (built by
 * buildGameStatsContextForAgent). This agent adds the per-game layer so the
 * LLM can answer questions like "last 5 games", "stats in wins vs losses",
 * "how did Nate do this week", etc.
 *
 * Supported sports: Baseball, Softball, and Tennis (sports with sport_stats entries).
 */

import { supabase } from '../../../lib/supabase';
import { playerCardService } from '../../../services/playerCardService';
import { SPORT_STAT_KEYS } from '../../../data/sportStats';
import type { Sport } from '../../../types/core';
import type { AgentRunContext, AgentRunResult, CoachSteveAgent } from './types';
import { runHelpAgent } from './helpAgent';

// ---------------------------------------------------------------------------
// isStatsLookupIntent — regex fallback path only (used when router is off).
// Matches any question that is clearly about player statistics.
// ---------------------------------------------------------------------------

const STAT_KEYWORDS =
  /\b(stat(s|istic)?|batting|pitching|avg|average|era|whip|obp|slg|ops|rbi|home\s*run|homer|double|triple|single|stolen\s*base|strikeout|walks|hits?|runs?|hr|sb|bb|ip|innings?\s*pitched|1b|2b|3b|k s?|win|leader|top|most|fewest|highest|lowest|ranking|who\s+has|how\s+many|how\s+is)\b/i;

export function isStatsLookupIntent(text: string): boolean {
  return STAT_KEYWORDS.test(text);
}

// ---------------------------------------------------------------------------
// Per-game data fetch
// ---------------------------------------------------------------------------

interface GameRow {
  id: string;
  date: string;
  opponent_name: string;
  our_score: number | null;
  opponent_score: number | null;
}

/**
 * Fetch per-game stat breakdowns for the most recent N games and format them
 * as a context block the LLM can reason over.
 */
async function buildPerGameStatsContext(
  ctx: AgentRunContext,
  maxGames = 20,
): Promise<string | null> {
  const sport = ctx.team.sport as Sport;
  const sportStatKeys = SPORT_STAT_KEYS[sport];
  if (!sportStatKeys?.length) return null;

  try {
    // 1. Get recent games (with scores = stats were recorded).
    const { data: games, error: gErr } = await supabase
      .from('games')
      .select('id, date, opponent_name, our_score, opponent_score')
      .eq('team_id', ctx.team.id)
      .not('our_score', 'is', null)
      .order('date', { ascending: false })
      .limit(maxGames);

    if (gErr || !games?.length) return null;


    // 2. Fetch per-game stats via RPC — one row per (game_id, player_card_id)
    //    with all stats as a jsonb object. Avoids the PostgREST 1000-row limit
    //    that would hit when fetching raw sport_stats rows (6500+ per 20 games).
    const { data: pgRows, error: sErr } = await supabase
      .rpc('get_team_per_game_stats', { p_team_id: ctx.team.id, p_game_limit: maxGames });

    if (sErr || !pgRows?.length) return null;

    // 3. Build card → player name map.
    const cards = await playerCardService.getPlayerCardsForTeam(ctx.team.id);
    const cardToName = new Map<string, string>();
    for (const c of cards) {
      if (!c.player) continue;
      const label = `${c.player.first_name} ${c.player.last_name}`.trim();
      cardToName.set(c.id, c.player.number != null ? `${label} #${c.player.number}` : label);
    }

    // 4. Group RPC rows by game_id.
    const byGame = new Map<string, Map<string, Record<string, number>>>();
    for (const row of pgRows) {
      const gId = row.game_id as string;
      const cId = row.player_card_id as string;
      if (!byGame.has(gId)) byGame.set(gId, new Map());
      byGame.get(gId)!.set(cId, row.stats as Record<string, number>);
    }

    // 5. Format — games sorted newest first.
    const lines: string[] = [];
    for (const game of games) {
      const byCard = byGame.get(game.id);
      if (!byCard?.size) continue;

      const result =
        game.our_score != null && game.opponent_score != null
          ? `${game.our_score > game.opponent_score ? 'W' : game.our_score < game.opponent_score ? 'L' : 'T'} ${game.our_score}-${game.opponent_score}`
          : '';
      lines.push(`${game.date} vs ${game.opponent_name}${result ? ` (${result})` : ''}:`);

      for (const [cardId, statsObj] of byCard) {
        const name = cardToName.get(cardId) ?? cardId;
        const parts = Object.entries(statsObj)
          .filter(([, v]) => Number(v) !== 0)
          .map(([k, v]) => `${k} ${v}`)
          .join(' | ');
        if (!parts) continue;
        lines.push(`  ${name}: ${parts}`);
      }
    }

    if (!lines.length) return null;

    return [
      `--- PER-GAME STATS — ${sport} (last ${games.length} game${games.length !== 1 ? 's' : ''} with scores, newest first) ---`,
      ...lines,
      '---',
    ].join('\n');
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Agent definition
// ---------------------------------------------------------------------------

export const statsAgent: CoachSteveAgent = {
  name: 'stats',
  description:
    'Retrieves player statistics from the database and lets the LLM interpret them. Handles season totals, per-game breakdowns, rankings, player lookups, and any stat question. Baseball, Softball, and Tennis only.',
  applicableSports: ['Baseball', 'Softball', 'Tennis'],
  match: (text) => isStatsLookupIntent(text),

  async handle(text, ctx): Promise<AgentRunResult> {
    const sport = ctx.team.sport as Sport;
    if (sport !== 'Baseball' && sport !== 'Softball' && sport !== 'Tennis') {
      // Stat DB entries only exist for these sports; let helpAgent answer generically.
      return runHelpAgent(text, ctx);
    }

    // Fetch per-game breakdown and inject as extra context for the LLM.
    // Season totals are already in ctx.teamContextForAgent.
    const perGameContext = await buildPerGameStatsContext(ctx);

    return runHelpAgent(text, ctx, perGameContext ?? undefined);
  },
};
